/**
 * lib/clockingRegister.ts
 * ------------------------------------------------------------------
 * HR DOMAIN — the Clocking sheet: one row per person, one cell per day,
 * coloured by how late they were. Squares chosen by the school in Oct 2026
 * (after trying a day timeline) because they show patterns at a glance;
 * since Nov 2026 each cell also prints the day's times, Monday to Saturday,
 * by the week or by the month.
 *
 * This decides what each cell IS; features/hr/ClockingBoard draws it.
 * The verdict on a day (in, out, minutes late) is lib/clockingRules.
 * ------------------------------------------------------------------ */
import { dayVerdict, localHHMM, weekday, type ClockingRules, type DayVerdict, SCHOOL_CLOCKING } from '@/lib/clockingRules';
import type { AttendanceDayStatus, AttendancePunch } from '@/lib/hr';

export function addDays(date: string, n: number): string {
  const d = new Date(`${date}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

/** Every date of a month, YYYY-MM-DD. `month` is "YYYY-MM". */
export function monthDays(month: string): string[] {
  const [y, m] = month.split('-').map(Number);
  const n = new Date(Date.UTC(y, m, 0)).getUTCDate();
  return Array.from({ length: n }, (_, i) => `${month}-${String(i + 1).padStart(2, '0')}`);
}

/** "YYYY-MM" a number of months away. */
export function shiftMonth(month: string, by: number): string {
  const [y, m] = month.split('-').map(Number);
  const d = new Date(Date.UTC(y, m - 1 + by, 1));
  return d.toISOString().slice(0, 7);
}

/** The Monday of a date's week. A Sunday belongs to the week it ends. */
export function weekStart(date: string): string {
  return addDays(date, -((weekday(date) + 6) % 7));
}

/** Monday to Saturday of the week starting `monday`. */
export function weekDays(monday: string): string[] {
  return Array.from({ length: 6 }, (_, i) => addDays(monday, i));
}

/** Every Monday-to-Saturday date of a month. */
export function monthSheetDays(month: string): string[] {
  return monthDays(month).filter((d) => weekday(d) !== 0);
}

/** How late, in the narrow month cell: "26 min", "1 h 05", "2 h". */
export function shortLate(m: number): string {
  if (m < 60) return `${m} min`;
  const r = m % 60;
  return r ? `${Math.floor(m / 60)} h ${String(r).padStart(2, '0')}` : `${m / 60} h`;
}

/** On approved leave that day. */
export function onLeaveOn(
  leave: { staffId: string; startDate: string; endDate: string; status: string }[],
  staffId: string,
  date: string,
): boolean {
  return leave.some((l) => l.staffId === staffId && l.status === 'approved'
    && l.startDate.slice(0, 10) <= date && l.endDate.slice(0, 10) >= date);
}

export type CellKind =
  /** Sunday, a public holiday, a Saturday or a school-holiday day, with
   *  nobody expecting a punch. */
  | 'off'
  /** After the last day uploaded — not known yet, not absent. */
  | 'notYet'
  | 'leave'
  /** A working weekday with no punch and no leave. */
  | 'noPunch'
  | 'onTime'
  /** Up to 15 minutes late. */
  | 'late15'
  /** 16 to 45 minutes. */
  | 'late45'
  /** Over 45 minutes. */
  | 'lateMore';

export interface RegisterCell {
  kind: CellKind;
  /** A clock-in or clock-out is missing — drawn hatched. */
  missing: boolean;
  /** What to print in the square: minutes late past 15, hours past 45. */
  text: string;
  v: DayVerdict | null;
  /** Every punch of the day, "HH:MM", in order. The cell prints the first and
   *  last; its tooltip lists them all when there were more than two. */
  times: string[];
}

/** What one square is. */
export function registerCell(
  date: string,
  rec: { punches: AttendancePunch[]; status?: AttendanceDayStatus } | undefined,
  ctx: { lastUploaded: string; onLeave: boolean; rules?: ClockingRules },
): RegisterCell {
  const rules = ctx.rules ?? SCHOOL_CLOCKING;
  const dow = weekday(date);
  const holiday = !!rules.isHoliday?.(date);
  if (rec && rec.punches.length) {
    const v = dayVerdict({ date, ...rec }, rules);
    const m = v.lateMinutes;
    const kind: CellKind = !v.late ? 'onTime' : m <= 15 ? 'late15' : m <= 45 ? 'late45' : 'lateMore';
    const text = kind === 'late45' ? String(m) : kind === 'lateMore' ? `${Math.round((m / 60) * 10) / 10}h` : '';
    return { kind, missing: v.missingPunch, text, v, times: rec.punches.map((p) => localHHMM(p.at)).sort() };
  }
  if (date > ctx.lastUploaded) return { kind: 'notYet', missing: false, text: '', v: null, times: [] };
  if (ctx.onLeave && dow !== 0 && !holiday) return { kind: 'leave', missing: false, text: 'L', v: null, times: [] };
  const schoolHoliday = !!rules.inTerm && !rules.inTerm(date);
  if (dow === 0 || dow === 6 || holiday || schoolHoliday) return { kind: 'off', missing: false, text: '', v: null, times: [] };
  return { kind: 'noPunch', missing: false, text: '✕', v: null, times: [] };
}

/** A square's tooltip, in words. */
export function cellTitle(date: string, c: RegisterCell, holidayName?: string): string {
  const when = new Date(`${date}T12:00:00Z`).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC' });
  const v = c.v;
  if (v) {
    const parts = [
      v.firstIn ? `In ${v.firstIn}` : 'No clock-in',
      v.lastOut && v.firstIn ? `Out ${v.lastOut}` : v.lastOut ? `Out ${v.lastOut}` : 'no clock-out',
    ];
    if (v.late) parts.push(`${v.lateMinutes} min late`);
    if (c.times.length > 2) parts.push(`All punches ${c.times.join(', ')}`);
    return `${when}${holidayName ? ` (${holidayName})` : ''} · ${parts.join(' · ')}`;
  }
  const word: Record<CellKind, string> = {
    off: holidayName ?? (weekday(date) === 0 ? 'Sunday' : weekday(date) === 6 ? 'Saturday, not in' : 'School holidays, not in'),
    notYet: 'Not uploaded yet', leave: 'On leave', noPunch: 'No punch',
    onTime: '', late15: '', late45: '', lateMore: '',
  };
  return `${when} · ${word[c.kind]}`;
}
